import cart from '@/store/common/cart'
import apiClient from "@/utils/cb-client"
import Transformer from "@/utils/transformer"
import Vue from 'vue'
import CartHelper from '@/utils/cart-helper'
import HostedPageHelper from "@/utils/hosted-page-helper"
import CBNotification from "@/models/cb-notification";
import { termTranslate, pluralize } from "@/utils/helper-functions"
import { ADD_BILLING_ADDRESS } from "@/store/common/customer";
import OrderEstimateHolder from "@/models/order-estimate-holder"
import { GIFT_ACC_FIELDS } from "@/utils/gift-sections-resolver"
import { threedsNeeded, intentFlowEnabled } from "@/components/modules/hosted-pages/LazyLoadPaymentComponents"
import LogTracker from '@/utils/log-helper';

const state = {
  shipping_address: {},
  // all details will be present if is_cart_set is true
  is_cart_set: false,
  payment_method: undefined,
  subscription: {},
  invoice: {},
  pm_subscription_level: false,
  tos_agreed: false,
  order_estimate_holder: undefined,
  cart_combined: false,
  consolidated_items: [],
  is_gift: false,
  // receiver's account information
  gift: {
    data: {}
  },
  receiver: {
    existing: false,
    handle: undefined,
    data: {}
  }
}

const SET_SHIPPING_ADDRESS = "SET_SHIPPING_ADDRESS";
const SET_PAYMENT_METHOD = "SET_PAYMENT_METHOD";
const SET_SUBSCRIPTION_FIELDS = "SET_SUBSCRIPTION_FIELDS";
const SET_INVOICE_FIELDS = "SET_INVOICE_FIELDS";
const ENABLE_COPY_TO_BILLING = "ENABLE_COPY_TO_BILLING";
const ATTACH_PM_SUBSCRIPTION_LEVEL = "ATTACH_PM_SUBSCRIPTION_LEVEL";
const SET_TOS_AGREED = "SET_TOS_AGREED";
const SET_ORDER_ESTIMATE_HOLDER = "SET_ORDER_ESTIMATE_HOLDER";
const SET_CART_COMBINED = "SET_CART_COMBINED";
const SET_CONSOLIDATED_ITEMS = "SET_CONSOLIDATED_ITEMS";
const LINK_ESITMATE = "LINK_ESITMATE";
const LINK_ESITMATE_ONE_TIME = "LINK_ESITMATE_ONE_TIME";
const SET_PLAN_SETUP = "SET_PLAN_SETUP";
const MARK_AS_GIFT = "MARK_AS_GIFT";
const ADD_RECEIVER_DETAILS = "ADD_RECEIVER_DETAILS";
const ADD_GIFT_DETAILS = "ADD_GIFT_DETAILS";
const MARK_AS_EXISTING = "MARK_AS_EXISTING";
const SYNC_GIFT_DETAILS = "SYNC_GIFT_DETAILS";
const SET_IS_CART_SET = "SET_IS_CART_SET";
const SET_RECOMMENDED_ADDONS_V2 = "SET_RECOMMENDED_ADDONS_V2";

var getEstimatePromise = (state, rootState, getters) => {
  if(getters.is_checkout_existing) {
    return apiClient.cart.update_subscription(
      {id: getters.subscription_handle, hp_token: rootState.auth.hpToken},
      Transformer.estimateRequestForUpdateSubscription(state, rootState, getters)
    )
  } else if(getters.is_checkout_gift) {
    return !rootState.auth.authenticated
      ? apiClient.cart.create_gift_subscription(
          {hp_token: rootState.auth.hpToken},
          Transformer.estimateGiftRequest(state, rootState, getters)
        )
      : apiClient.cart.create_gift_subscription_for_customer(
          {id: getters.customer_handle, hp_token: rootState.auth.hpToken},
          Transformer.estimateGiftRequestForCustomer(state, rootState, getters)
        );
  } else if (getters.is_checkout_one_time) {
    return (getters.is_items_model) ? apiClient.estimates.create_invoice_for_items(
      { hp_token: rootState.auth.hpToken },
      Transformer.estimateCreateInvoiceItemsRequest(state, rootState, getters)) :
      apiClient.estimates.create_invoice(
        {hp_token: rootState.auth.hpToken },
        Transformer.estimateCreateInvoiceRequest(state, rootState, getters));
  } else {
    return !rootState.auth.authenticated
      ? apiClient.cart.create_subscription(
          {hp_token: rootState.auth.hpToken},
          Transformer.estimateRequest(state, rootState, getters)
        )
      : apiClient.cart.create_subscription_for_customer(
          {id: getters.customer_handle, hp_token: rootState.auth.hpToken},
          Transformer.estimateRequestForCustomer(state, rootState, getters)
        );
  }
}

const actions = {
  calculate_estimate({commit, state, getters, rootState, dispatch}, options) {
    dispatch('set_order_items', options);
  },

  set_order_items({commit, state, getters, rootState, dispatch}, options) {
    var options = options || {};
    getEstimatePromise(state, rootState, getters, options).then(data => {
      // Set the estimates
      // get order item if present and compare for quantity and replace it
      let orderEstimateHolder =  new OrderEstimateHolder(data, getters);
      let orderItem = !getters.is_checkout_one_time ?
        HostedPageHelper.constructOrderItemsFromCart(data, rootState.order.cart.orderItems[0]) :
        HostedPageHelper.constructOneTimeOrderItemsFromCart(data.order);

      //will be handled properly during multi coupon support
      // if(getters.is_checkout_existing && rootState.checkout.data.coupon) {
      //   orderItem.coupon = rootState.checkout.data.coupon;
      // }
      dispatch('replace_order_item', orderItem)
        .then(() => new LogTracker(getters, dispatch).setOrderEstimateHolder(orderEstimateHolder).init());

      // if(!state.order_estimate_holder) { // Commented for fixing CHKOUTENGG-16275
        // replace order item will update rootState orderItem, so take it again
      commit(SET_CART_COMBINED, CartHelper.canCombineCart(getters, orderEstimateHolder, rootState.order.cart.orderItems[0]));
      // }
      commit(SET_ORDER_ESTIMATE_HOLDER, orderEstimateHolder);

      if(state.cart_combined && !getters.is_checkout_one_time) {
        commit(SET_PLAN_SETUP, HostedPageHelper.constructPlanSetup(getters.plan_setup_line_item))
        commit(LINK_ESITMATE, {planLineItem: getters.plan_line_item, addonLineItems: getters.addon_line_items})
      } else {
        commit(LINK_ESITMATE_ONE_TIME, { addonLineItems: getters.addon_line_items })
      }
      commit(SET_CONSOLIDATED_ITEMS, CartHelper.consolidateEstimate(getters.cart_items, getters.taxes, getters.discounts, getters.coupon_list));
      if(options.resolve) {
        options.resolve(true);
      }
    })
    .catch(error => {
      if(options.reject) {
        options.reject(error);
      }
      else {
        // These errors should not be happening. These errors will occur only if we have missed some use cases.
        dispatch('set_current_notification', new CBNotification('error', error));
        error.notifyBackend = true;
        logger.e(error);
      }
    });
  },

  add_shipping_address_details_from_customer({commit, state, rootState, getters, dispatch}, options = {}) {
    if(!state.shipping_address || Object.keys(state.shipping_address).length == 0) {
      if(rootState.customer.shipping_address_list.length > 0){
        commit(SET_SHIPPING_ADDRESS, rootState.customer.shipping_address_list[0]);
        dispatch('calculate_estimate');
      }
      else if(Object.keys(rootState.customer.billing_address).length > 0 && (options.new_customer ? true : getters.billing_address_already_present) && getters.ship_to_billing_enabled){
        commit(SET_SHIPPING_ADDRESS, rootState.customer.billing_address);
        dispatch('add_shipping_address', rootState.customer.billing_address);
        dispatch('calculate_estimate');
      }
    }
  },

  add_customer_details_to_order({commit, state, rootState, getters, dispatch}, options = {}) {
    if(!state.is_gift) {
      dispatch('add_shipping_address_details_from_customer', options);
    }

    if(getters.mpm_supported && options && options.subscription_pm_id) {
      const pm = getters.fetch_pm(options.subscription_pm_id);
      dispatch('log_selected_payment_method', pm);
      commit(SET_PAYMENT_METHOD, pm);
      if(state.payment_method) {
        commit(ATTACH_PM_SUBSCRIPTION_LEVEL, true);
      }
    }
    if(!state.payment_method && getters.primary_pm) {
      const pm = getters.primary_pm;
      dispatch('log_selected_payment_method', pm);
      commit(SET_PAYMENT_METHOD, pm);
    }
  },

  add_gift_details({commit, state}, data) {
    commit(ADD_GIFT_DETAILS, data);
    commit(SYNC_GIFT_DETAILS)
  },

  mark_receiver_as_existing({commit, state}, data) {
    commit(MARK_AS_EXISTING, data);
    // clear account data if present
    if(data.existing) {
      commit(ADD_RECEIVER_DETAILS, undefined);
    }
  },

  // Can be reused for gift_checkout
  set_shipping_address_to_order({commit, state, dispatch, getters}, {shipping_address, skip_est_calc, resolve, reject}) {
    let old = state.shipping_address;
    commit(SET_SHIPPING_ADDRESS, shipping_address);
    if(!skip_est_calc) {
      let promise = new Promise((res, rej) => {
        dispatch('calculate_estimate', { resolve: res, reject: rej});
      }).then(data => resolve && resolve(data)).catch(error => {
        if(reject) {
          commit(SET_SHIPPING_ADDRESS, old);
          if(getters.ship_to_billing_enabled) {
            commit(ADD_BILLING_ADDRESS, old);
          }
          reject(error);
        }
      });
    }
  },

  resolve_and_add_shipping_address({dispatch, state}, shipping_address) {
    if(!state.is_gift) {
      dispatch('add_shipping_address', shipping_address)
    }
  },

  resolve_and_add_customer_data({dispatch, state, commit}, customerData) {
    if(!state.is_gift) {
      dispatch('add_customer_data', customerData)
    }
    else {
      commit(ADD_RECEIVER_DETAILS, customerData);
    }
  },

  set_payment_method_to_order({dispatch, commit, state}, payment_method) {
    dispatch('log_selected_payment_method', payment_method);
    commit(SET_PAYMENT_METHOD, payment_method);
  },

  add_subscription_fields({commit, state}, subscription_fields) {
    commit(SET_SUBSCRIPTION_FIELDS, subscription_fields)
  },

  add_invoice_fields({commit, state}, invoice_fields) {
    commit(SET_INVOICE_FIELDS, invoice_fields)
  },

  enable_copy_to_billing({commit, state}, enabled) {
    commit(ENABLE_COPY_TO_BILLING, enabled);
  },

  mark_as_gift({commit, state, dispatch}) {
    commit(MARK_AS_GIFT, true);
    dispatch('enable_copy_to_billing', false);
  },

  // Need to check this behaviour for gift subscription
  attach_pm_subscription_level({commit, state}, enabled) {
    commit(ATTACH_PM_SUBSCRIPTION_LEVEL, enabled);
  },
  trigger_cart_change({commit, state, dispatch, getters}) {
    if(getters.is_checkout_new && !state.payment_method) {
      dispatch('determine_steps');
    }
    if(!state.is_cart_set) {
      commit(SET_IS_CART_SET, true);
    }
  },
  set_tos_agreed({commit,state}, enabled) {
    commit(SET_TOS_AGREED, enabled);
  },

  fetch_recommended_addons({commit}, planId) {
    return apiClient.item_price.fetch_recommended_addons({ itemPriceId: planId })
    .then((addons) => {
      commit(SET_RECOMMENDED_ADDONS_V2, addons.list.map(elm => elm.item_price));
    })
    .catch((err) => {
			logger.e(error);
		});;
  }

}

const mutations = {
  [SET_IS_CART_SET] (state, is_cart_set) {
    Vue.set(state, "is_cart_set", is_cart_set);
  },
  [SET_SHIPPING_ADDRESS] (state, shipping_address) {
    Vue.set(state, "shipping_address", shipping_address);
  },
  [SET_PAYMENT_METHOD] (state, payment_method) {
    Vue.set(state, "payment_method", payment_method);
  },
  [SET_SUBSCRIPTION_FIELDS] (state, subscription_fields) {
    Vue.set(state, "subscription", subscription_fields);
  },
  [SET_INVOICE_FIELDS] (state, invoice_fields) {
    Vue.set(state, "invoice", invoice_fields);
  },
  [ENABLE_COPY_TO_BILLING] (state, enabled) {
    Vue.set(state, 'ship_to_billing_enabled', enabled);
  },
  [ATTACH_PM_SUBSCRIPTION_LEVEL] (state, enabled) {
    Vue.set(state, 'pm_subscription_level', enabled);
  },
  [SET_TOS_AGREED] (state, enabled) {
    Vue.set(state, "tos_agreed", enabled);
  },
  [SET_ORDER_ESTIMATE_HOLDER] (state, orderEstimateHolder) {
    Vue.set(state, 'order_estimate_holder', orderEstimateHolder);
  },
  [SET_CART_COMBINED] (state, cartCombined) {
    state.cart_combined = cartCombined;
  },
  [SET_CONSOLIDATED_ITEMS] (state, consolidated_items) {
    Vue.set(state, "consolidated_items", consolidated_items);
  },
  [LINK_ESITMATE] (state, {planLineItem, addonLineItems}) {
    Vue.set(state.cart.orderItems[0].plan, 'lineItem', planLineItem);
    state.cart.orderItems[0].addons.forEach(function(addon){
      // Should have been inside cart.js
      Vue.set(addon, 'lineItem', addonLineItems.filter(li => li.entity_id == addon.id)[0]);
    });
  },
  [LINK_ESITMATE_ONE_TIME](state, { addonLineItems }) {
    state.cart.orderItems[0].addons.forEach(function (addon) {
      Vue.set(addon, 'lineItem', addonLineItems.filter(li => li.entity_id == addon.id)[0]);
    });
  },
  [SET_PLAN_SETUP] (state, planSetupItem) {
    Vue.set(state.cart.orderItems[0], 'plan_setup', planSetupItem);
  },
  [MARK_AS_GIFT] (state, giftable) {
    state.is_gift = giftable;
  },
  [ADD_RECEIVER_DETAILS] (state, data) {
    if(!data) {
      // clear the data if its undefined
      Vue.set(state.receiver, 'data', {});
    }
    else {
      // set fields other than first_name, last_name and email
      Object.keys(data).filter(i => GIFT_ACC_FIELDS.indexOf(i.name) == -1).forEach(k => {
        state.receiver.data[k] = data[k];
      });
    }
  },
  [MARK_AS_EXISTING] (state, {existing, handle}) {
    state.receiver.existing = existing;
    state.receiver.handle = handle;
  },
  [ADD_GIFT_DETAILS] (state, data) {
    Vue.set(state.gift, 'data', data);
  },
  [SYNC_GIFT_DETAILS](state) {
    Vue.set(state.receiver.data, 'email', state.gift.data.email);
    Vue.set(state.receiver.data, 'first_name', state.gift.data.first_name);
    Vue.set(state.receiver.data, 'last_name', state.gift.data.last_name);
  },
  [SET_RECOMMENDED_ADDONS_V2] (state, recommended_addons_v2) {
    Vue.set(state, "recommended_addons_v2", recommended_addons_v2);
  },
}

const getters = {
  plan_setup_line_item: (state, getters) => {
    return getters.get_line_items("plan_setup")[0]
  },
  plan_line_item: (state, getters) => {
    return getters.is_items_model ? getters.get_line_items("plan_item_price")[0] :
    getters.get_line_items("plan")[0];
  },
  addon_line_items: (state, getters) => {
    return getters.is_items_model ? getters.get_line_items("addon_item_price") : 
    getters.get_line_items("addon");
  },
  gift_receiver: (state, getters) => {
    return getters.is_checkout_gift && state.receiver;
  },
  gift_details: (state, getters) => {
    return getters.is_checkout_gift && state.gift.data;
  },
  get_line_items: (state, getters) => (entityType) => {
    return getters.line_items.filter(item => item.entity_type == entityType);
  },
  get_line_item_by_entity_id: (state, getters) => (entityId) => {
    return getters.line_items.find(item => item.entity_id == entityId);
  },
  line_items: (state, getters) => getters.estimate.line_items || [],
  order_items: state => state.cart.orderItems[0],
  selected_payment_method: state => state.payment_method,
  selected_shipping_address: state => state.shipping_address,
  pm_at_subscription_level: state => state.pm_subscription_level,
  tos_agreed: (state) => state.tos_agreed,
  is_cart_with_estimate: (state) => state.cart_combined,
  ship_to_billing_enabled: state => state.ship_to_billing_enabled,
  order_estimate_holder: state => state.order_estimate_holder || {},
  estimate: state => (state.order_estimate_holder && state.order_estimate_holder.estimate) || {},
  is_tax_inclusive: (state, getters) => getters.estimate.price_type == "tax_inclusive",
  is_tax_exclusive: (state, getters) => getters.estimate.price_type == "tax_exclusive",
  discounts: (state, getters) => (state.order_estimate_holder && state.order_estimate_holder.getDiscounts()) || [],
  taxes: (state, getters) => getters.estimate.taxes || [],
  currency_code: (state, getters) => {
    return getters.estimate.currency_code ||
      (getters.cart_plan && getters.cart_plan.details.currency_code) ||
      (getters.order_estimate_holder && getters.order_estimate_holder.subscriptionEstimate && getters.order_estimate_holder.subscriptionEstimate.currency_code)
  },
  amount_due: (state, getters) => getters.estimate.amount_due || 0,
  current_amount_due: (state, getters) => ((getters.order_estimate_holder.immediateEstimate && getters.order_estimate_holder.immediateEstimate.amount_due) || 0),
  estimate_total: (state, getters) => getters.estimate.total || 0,
  total_credits: (state, getters) => CartHelper.getTotalCreditsFromDiscounts(getters.estimate.discounts, getters.estimate.credits_applied, getters.estimate.amount_paid),
  consolidated_items: state => state.consolidated_items,
  consolidated_discount: (state, getters) => getters.consolidated_items.filter(i => i.type == "discount")[0],
  is_discount_present: (state, getters) => (getters.coupon_list.length > 0) && getters.consolidated_discount,
  consolidated_tax: (state, getters) => getters.consolidated_items.filter(i => i.type == "tax")[0],
  cart_items_loaded: (state, getters) => state.is_cart_set && getters.cart_items && getters.cart_items.some(i => i.type == "plan_setup" ? true : i.details),
  order_cart_items: (state, getters) => getters.cart_items,
  cart_plan: (state, getters) => getters.cart_items && getters.cart_items.filter(i => i.type == "plan" && i.details)[0],
  hasTrialPeriod: (state, getters) => {
    //if current plan is active, then not showing the trial note
    let handle = getters.subscription_handle;
    if(handle && getters.subscriptions && getters.subscriptions.length > 0) {
      let sub = getters.subscriptions.filter(s => s.id == handle)[0];
      if(sub && (sub.status == "active" || sub.status == "non_renewing")) {
        return false;
      }
    }
    // trial overrided
    if(state.subscription.trial_end){
      return parseInt(state.subscription.trial_end) > 0;
    }
    return getters.is_plan_having_trial;
  },
  is_plan_having_trial: (state, getters, rootState) => {
    if (getters.can_allow('line_items.hide_zero')) {
      let plan = rootState.order.cart.orderItems[0].plan;
      return plan && plan.trial_period > 0;
    }
    return getters.cart_plan && getters.cart_plan.details.trial_period > 0;
  },
  is_zero_dollar_plan: (state,getters) => getters.order_items && getters.order_items.zero_dollar_plan,
  plan_placeholder: (state, getters) => {
   let cp = getters.cart_plan
   let out = {
     plan_name: cp && cp.details.name,
     billing_period: cp && cp.details.period + ' ' + termTranslate(cp.details.period_unit, cp.details.period > 1, getters.stranslate),
    };
   // trial overrided
   const locale = getters.init_info_locale && getters.init_info_locale.slice(0,2);
   let unit;
   if(state.subscription.trial_end && parseInt(state.subscription.trial_end) > 0) {
    let days = Math.round((new Date(parseInt(state.subscription.trial_end)*1000) - new Date())/(1000*60*60*24));
    unit = pluralize('term.day', days, locale);
    out['plan_trial_period'] = days + '-' + getters.stranslate(unit);
   } else {
    unit = cp && pluralize(`term.${cp.details.trial_period_unit}`, cp.details.trial_period, locale);
    out['plan_trial_period'] = cp && cp.details.trial_period + '-' +  getters.stranslate(unit);
   }
   return out;
  },
  threeDSNeeded: (state, getters) => {
    return state.payment_method && state.payment_method.type =="card" && !getters.customer_autocollection_off && threedsNeeded(state.payment_method.gateway_account_id, state.payment_method.type, state.payment_method);
  },
  intentFlowEnabled: (state, getters) => {
    return state.payment_method && state.payment_method.type =="card" && intentFlowEnabled(state.payment_method.gateway_account_id, state.payment_method.type);
  },
  recommended_addons: (state, getters) => {
    if(!(state.cart.orderItems && state.cart.orderItems[0])) {
      return [];
    }
    let plan = state.cart.orderItems[0].plan;
    let applied_addons = state.cart.orderItems[0].addons || [];
    let plan_attached_addons = plan && plan.details && plan.details.attached_addons|| [];
    let plan_attached_items = plan && plan.details && plan.details.attached_items || []

    if(plan_attached_items.length){
      plan_attached_items = plan_attached_items.map(({attached_item}) => attached_item)

      let plan_recommended_items = plan_attached_items.filter(i => i.type == "recommended") || [];
      const appliedItemPriceIds = applied_addons.map(addon => addon.id);

      // Filter applied attached items
      return plan_recommended_items.filter(attachedItem => {
        return attachedItem.item_prices && attachedItem.item_prices.every(({item_price: itemPrice}) => appliedItemPriceIds.indexOf(itemPrice.id) == -1)
      })
    } else {
      let plan_recommended_addons = plan_attached_addons.filter(i => i.type == "recommended") || [];
      return plan_recommended_addons.filter(i => !applied_addons.some( e => e.id == i.id)) || [];
    }
  },
  recommended_addons_v2: (state, getters) => {
    let recommendedAddons = state.recommended_addons_v2;

    //Get already applied addons
    let applied_addons = getters.order_items.addons || [];
    const appliedItemIds = applied_addons.map(addon => addon.item_id);

    //Filter already applied addon item from recommendations
    return recommendedAddons?.filter(addon => {
      return appliedItemIds.indexOf(addon.item_id) == -1;
    });
  },
  is_ideal_payment: state => state.payment_method && state.payment_method.type =="ideal",
  is_sofort_payment: state => state.payment_method && state.payment_method.type =="sofort",
  is_bancontact_payment: state => state.payment_method && state.payment_method.type =="bancontact",
  is_google_payment: state => state.payment_method && state.payment_method.type =="google_pay",
  is_applepay_payment: state => state.payment_method && state.payment_method.type =="apple_pay",
  is_paypal_payment: state => state.payment_method && state.payment_method.type =="paypal_express_checkout",
  is_giropay_payment: state => state.payment_method && state.payment_method.type =="giropay",
  is_netbanking_emandate_payment: state => state.payment_method && state.payment_method.type =="netbanking_emandates",
  is_upi_payment: state => state.payment_method && state.payment_method.type =="upi",
  is_direct_debit_payment: state => state.payment_method && state.payment_method.type =="direct_debit",
  is_instant_direct_debit: state => state.payment_method && ["faster_payments", "pay_to", "sepa_instant_transfer"].includes(state.payment_method.type),
  is_boleto_payment: state => state.payment_method && state.payment_method.type =="boleto",
  is_venmo_payment: state => state.payment_method && state.payment_method.type =="venmo",
  is_klarna_pay_now_payment: state => state.payment_method && state.payment_method.type =="klarna_pay_now",
}

export default {
  state,
  actions,
  mutations,
  getters,
  modules: {
    cart
  }
}
